import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface RuleRow {
  readonly ruleId: string;
  readonly ruleCode: string;
  readonly ruleName: string;
  readonly ruleCategory: string;
  readonly priority: string;
  readonly status: string;
  readonly approvalStatus: string;
}

interface CategoryOption {
  readonly categoryCode: string;
  readonly categoryName: string;
}

export function M24RuleRepositoryPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [rules, setRules] = useState<RuleRow[]>([]);
  const [total, setTotal] = useState(0);
  const [category, setCategory] = useState('');
  const [categories, setCategories] = useState<CategoryOption[]>([]);
  const [error, setError] = useState<string | null>(null);
  // Apart from `error`, which every rule reload clears.
  const [categoriesError, setCategoriesError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    apiRequest<{ data: CategoryOption[] }>('/v1/rule-categories', { token })
      .then((r) => { if (alive) setCategories(r.data); })
      .catch((e) => { if (alive) setCategoriesError((e as Error).message); });
    return () => { alive = false; };
  }, [token]);

  const load = useCallback((): void => {
    setLoading(true);
    const query = category ? `?ruleCategory=${encodeURIComponent(category)}` : '';
    apiRequest<{ data: RuleRow[]; total: number }>(`/v1/rules${query}`, { token })
      .then((r) => { setRules(r.data); setTotal(r.total); setError(null); })
      .catch((e) => setError((e as Error).message))
      .finally(() => setLoading(false));
  }, [token, category]);

  useEffect(() => { load(); }, [load]);

  function runWorkflow(ruleId: string, action: 'submit' | 'approve' | 'reject'): void {
    setBusy(true);
    setNotice(null);
    setError(null);
    const body = action === 'reject'
      ? { reason: window.prompt(t('m24.repository.rejectPrompt')) ?? 'Rejected' }
      : undefined;
    apiRequest(`/v1/rules/${encodeURIComponent(ruleId)}/${action}`, { method: 'POST', token, body })
      .then(() => { setNotice(t(action === 'submit' ? 'm24.repository.noticeSubmitted' : action === 'approve' ? 'm24.repository.noticeApproved' : 'm24.repository.noticeRejected')); load(); })
      .catch((e) => setError((e as Error).message))
      .finally(() => setBusy(false));
  }

  return (
    <main aria-label="rule-repository">
      <h1>{t('m24.repository.title')}</h1>
      <label htmlFor="category-filter">{t('m24.repository.filterByCategory')}</label>
      <select id="category-filter" value={category} onChange={(e) => setCategory(e.target.value)}>
        <option value="">{t('m24.repository.allCategories')}</option>
        {categories.map((c) => <option key={c.categoryCode} value={c.categoryCode}>{c.categoryName}</option>)}
      </select>
      {categoriesError && <p role="alert">{categoriesError}</p>}
      {notice && <p role="status">{notice}</p>}
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('m24.repository.loading')}</p> : (
        <>
          <p aria-label="rule-count">{t('m24.repository.ruleCount', { count: total })}</p>
          <table aria-label="rule-table">
            <thead>
              <tr><th>{t('m24.repository.colCode')}</th><th>{t('m24.repository.colName')}</th><th>{t('m24.repository.colCategory')}</th><th>{t('m24.repository.colPriority')}</th><th>{t('m24.repository.colStatus')}</th><th>{t('m24.repository.colApproval')}</th><th>{t('m24.repository.colActions')}</th></tr>
            </thead>
            <tbody>
              {rules.map((r) => (
                <tr key={r.ruleId}>
                  <td>{r.ruleCode}</td>
                  <td>{r.ruleName}</td>
                  <td>{r.ruleCategory}</td>
                  <td>{r.priority}</td>
                  <td>{r.status}</td>
                  <td>{r.approvalStatus}</td>
                  <td>
                    <button type="button" aria-label={`submit-${r.ruleId}`} disabled={busy} onClick={() => runWorkflow(r.ruleId, 'submit')}>{t('m24.repository.submit')}</button>
                    <button type="button" aria-label={`approve-${r.ruleId}`} disabled={busy} onClick={() => runWorkflow(r.ruleId, 'approve')}>{t('m24.repository.approve')}</button>
                    <button type="button" aria-label={`reject-${r.ruleId}`} disabled={busy} onClick={() => runWorkflow(r.ruleId, 'reject')}>{t('m24.repository.reject')}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </main>
  );
}
